import {
  GlobalWorkerOptions,
  getDocument,
} from 'pdfjs-dist/legacy/build/pdf.mjs'

// A fax concerns each page, so previews are rendered per page. Serve the
// worker from the app origin (copied into public/ on postinstall) so a CDN
// compromise cannot inject script into this PHI session.
GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`

const DEFAULT_MAX_PAGES = 20
const RENDER_SCALE = 1

const buildDocumentOptions = (
  source: File | Blob | string,
  data?: Uint8Array,
): { url?: string; data?: Uint8Array; isEvalSupported: boolean } => {
  if (typeof source === 'string') {
    return { url: source, isEvalSupported: false as const }
  }
  return { data: data!, isEvalSupported: false as const }
}

export const isPdfFile = (mimeType?: string, fileName?: string): boolean =>
  mimeType?.includes('pdf') === true ||
  fileName?.toLowerCase().endsWith('.pdf') === true

export const isImageFile = (mimeType?: string): boolean =>
  mimeType?.startsWith('image/') === true

/**
 * Renders the pages of a PDF source to PNG data URLs. Returns one entry per
 * page (capped by maxPages). Resolves to an empty array on failure.
 */
export const renderPdfPages = async (
  source: File | Blob | string,
  maxPages = DEFAULT_MAX_PAGES,
): Promise<string[]> => {
  try {
    const data =
      typeof source === 'string'
        ? undefined
        : new Uint8Array(await source.arrayBuffer())
    const loadingTask = getDocument(buildDocumentOptions(source, data))
    const pdf = await loadingTask.promise
    const pageCount = Math.min(pdf.numPages, maxPages)
    const pages: string[] = []

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber)
      const viewport = page.getViewport({ scale: RENDER_SCALE })
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (!context) continue
      canvas.width = viewport.width
      canvas.height = viewport.height
      await page.render({ canvasContext: context, viewport }).promise
      pages.push(canvas.toDataURL('image/png'))
    }

    await pdf.destroy()
    return pages
  } catch {
    return []
  }
}

/**
 * Returns the number of pages in a PDF source, or 1 for non-PDF (single page)
 * sources. Resolves to 1 on failure so page-count math stays sane.
 */
export const getPdfPageCount = async (
  source: File | Blob | string,
): Promise<number> => {
  try {
    const data =
      typeof source === 'string'
        ? undefined
        : new Uint8Array(await source.arrayBuffer())
    const loadingTask = getDocument(buildDocumentOptions(source, data))
    const pdf = await loadingTask.promise
    const count = pdf.numPages
    await pdf.destroy()
    return count
  } catch {
    return 1
  }
}
